(() => {
  if (window.CaraLpSelection) return;

  const listeners = new Set();
  const products = {};
  const pendingLoads = {};
  let colors = [];
  let moneyFormat = '${{amount}}';
  let buttonLabel = 'Add to cart';
  let handle = '';
  let variantId = null;
  let qty = 1;
  let max = null;
  let cartCache = null;
  let cartCachePromise = null;
  let ready = false;
  let initPromise = null;

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function normalizeInventoryQuantity(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function formatMoney(cents) {
    const value = Number(cents);
    const amount = (value / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(value / 100));
    const withComma = amount.replace('.', ',');
    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function mapProductPayload(raw) {
    const featured = raw.featured_image;
    const featuredUrl = typeof featured === 'string'
      ? featured
      : (featured && (featured.src || featured)) || '';

    return {
      handle: raw.handle,
      title: raw.title,
      url: raw.url,
      featured_image: featuredUrl,
      variants: (raw.variants || []).map((variant) => {
        const variantImage = variant.featured_image;
        let imageUrl = '';
        if (typeof variantImage === 'string') {
          imageUrl = variantImage;
        } else if (variantImage && variantImage.src) {
          imageUrl = variantImage.src;
        } else if (variant.featured_media && variant.featured_media.preview_image) {
          imageUrl = variant.featured_media.preview_image.src || '';
        }

        const priceCents = typeof variant.price === 'number' ? variant.price : null;
        const compareCents = typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null;
        const priceAlreadyFormatted = typeof variant.price === 'string';

        return {
          id: variant.id,
          title: variant.title || variant.option1 || '',
          available: Boolean(variant.available),
          inventory_quantity: normalizeInventoryQuantity(variant.inventory_quantity),
          inventory_management: variant.inventory_management || null,
          inventory_policy: variant.inventory_policy || null,
          price: priceAlreadyFormatted
            ? variant.price
            : (priceCents !== null ? formatMoney(priceCents) : ''),
          compare_price: priceAlreadyFormatted
            ? (variant.compare_price || '')
            : (compareCents !== null && priceCents !== null && compareCents > priceCents
              ? formatMoney(compareCents)
              : ''),
          image: imageUrl
        };
      })
    };
  }

  function getColorMeta(productHandle) {
    return colors.find((item) => item.handle === productHandle) || null;
  }

  function applyCustomImage(product) {
    if (!product?.handle) return product;
    const customImage = getColorMeta(product.handle)?.customImage || '';
    if (customImage) product.featured_image = customImage;
    return product;
  }

  function getProduct(productHandle) {
    return productHandle ? products[productHandle] || null : null;
  }

  function getVariant(product, id) {
    if (!product || id == null) return null;
    return product.variants.find((item) => String(item.id) === String(id)) || null;
  }

  function getCartQuantityForVariant(id, cart) {
    if (!cart || !Array.isArray(cart.items) || !id) return 0;
    return cart.items.reduce((sum, item) => {
      if (String(item.variant_id) === String(id) || String(item.id) === String(id)) {
        return sum + (Number(item.quantity) || 0);
      }
      return sum;
    }, 0);
  }

  function getVariantMax(variant, cartQty = 0) {
    if (!variant) return null;
    const inventoryQty = normalizeInventoryQuantity(variant.inventory_quantity);
    if (inventoryQty === null) return null;
    if (!variant.inventory_management) return null;
    if (variant.inventory_policy === 'continue') return null;
    return Math.max(1, inventoryQty - cartQty);
  }

  async function getCart(force = false) {
    if (!force && cartCache) return cartCache;
    if (!force && cartCachePromise) return cartCachePromise;

    cartCachePromise = fetch(`${cartRoot()}cart.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      })
      .then((cart) => {
        cartCache = cart;
        return cart;
      })
      .catch(() => cartCache)
      .finally(() => {
        cartCachePromise = null;
      });

    return cartCachePromise;
  }

  function clampQty(nextQty, nextMax) {
    let value = Math.max(1, Number(nextQty) || 1);
    if (typeof nextMax === 'number' && nextMax > 0 && value > nextMax) {
      value = nextMax;
    }
    return value;
  }

  function getSnapshot() {
    const product = applyCustomImage(getProduct(handle));
    const variant = getVariant(product, variantId);
    return {
      handle,
      variantId,
      qty,
      max,
      available: Boolean(variant?.available),
      product,
      variant,
      colors: colors.slice(),
      buttonLabel,
      ready
    };
  }

  function emit() {
    const snapshot = getSnapshot();
    listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        console.warn('CaraLpSelection listener failed.', error);
      }
    });
    document.documentElement.dispatchEvent(
      new CustomEvent('cara-lp:selection-change', {
        bubbles: true,
        detail: snapshot
      })
    );
  }

  async function refreshMax(options = {}) {
    const { refreshCart = false } = options;
    const product = getProduct(handle);
    const variant = getVariant(product, variantId);
    if (!variant) {
      max = null;
      return null;
    }

    let cartQty = 0;
    const cart = await getCart(refreshCart);
    if (cart) cartQty = getCartQuantityForVariant(variant.id, cart);
    max = getVariantMax(variant, cartQty);
    qty = clampQty(qty, max);
    return max;
  }

  async function loadProduct(productHandle) {
    if (!productHandle) return null;
    if (products[productHandle]) return applyCustomImage(products[productHandle]);
    if (pendingLoads[productHandle]) return pendingLoads[productHandle];

    pendingLoads[productHandle] = fetch(`${cartRoot()}products/${productHandle}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed (${response.status})`);
        return response.json();
      })
      .then((raw) => {
        const mapped = applyCustomImage(mapProductPayload(raw));
        products[productHandle] = mapped;
        return mapped;
      })
      .finally(() => {
        delete pendingLoads[productHandle];
      });

    return pendingLoads[productHandle];
  }

  function pickVariant(product, preferredVariantId, preferredTitle) {
    if (!product?.variants?.length) return null;
    if (preferredVariantId != null) {
      const byId = getVariant(product, preferredVariantId);
      if (byId) return byId;
    }
    if (preferredTitle) {
      const byTitle = product.variants.find(
        (item) => String(item.title).toLowerCase() === String(preferredTitle).toLowerCase()
      );
      if (byTitle) return byTitle;
    }
    return product.variants.find((item) => item.available) || product.variants[0];
  }

  async function setColor(nextHandle, options = {}) {
    if (!nextHandle) return getSnapshot();
    const preferredVariantId = options.variantId ?? null;
    const previousTitle = options.preserveTitle !== false
      ? (getVariant(getProduct(handle), variantId)?.title || null)
      : null;

    const product = await loadProduct(nextHandle);
    if (!product) throw new Error('Unable to load product');

    handle = product.handle;
    const selected = pickVariant(product, preferredVariantId, previousTitle);
    variantId = selected ? selected.id : null;
    await refreshMax({ refreshCart: Boolean(options.refreshCart) });
    ready = true;
    emit();
    return getSnapshot();
  }

  async function setSize(nextVariantId) {
    const product = getProduct(handle);
    const variant = getVariant(product, nextVariantId);
    if (!variant) return getSnapshot();
    variantId = variant.id;
    await refreshMax();
    emit();
    return getSnapshot();
  }

  function setQty(nextQty) {
    qty = clampQty(nextQty, max);
    emit();
    return getSnapshot();
  }

  function seedProducts(seed = {}) {
    Object.keys(seed).forEach((key) => {
      if (seed[key]) products[key] = applyCustomImage(seed[key]);
    });
  }

  function init(options = {}) {
    if (options.moneyFormat) moneyFormat = options.moneyFormat;
    if (options.buttonLabel) buttonLabel = options.buttonLabel;
    if (Array.isArray(options.colors)) colors = options.colors.slice();
    if (options.products) seedProducts(options.products);

    const defaultHandle = options.defaultHandle
      || colors[0]?.handle
      || Object.keys(products)[0]
      || '';

    if (!defaultHandle) {
      ready = true;
      emit();
      return Promise.resolve(getSnapshot());
    }

    if (!initPromise) {
      initPromise = setColor(defaultHandle, {
        variantId: options.defaultVariantId || null,
        preserveTitle: false,
        refreshCart: false
      }).finally(() => {
        initPromise = null;
      });
    }

    return initPromise;
  }

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.cart) cartCache = event.detail.cart;
  });

  window.CaraLpSelection = {
    init,
    getSnapshot,
    isReady() {
      return ready;
    },
    subscribe(listener) {
      listeners.add(listener);
      if (ready) listener(getSnapshot());
      return () => listeners.delete(listener);
    },
    setColor,
    setSize,
    setQty,
    loadProduct,
    async syncCartMax(options = {}) {
      await refreshMax({ refreshCart: Boolean(options.refreshCart) });
      emit();
      return getSnapshot();
    },
    getCartQuantityForVariant,
    normalizeInventoryQuantity,
    formatMoney(cents) {
      return formatMoney(cents);
    }
  };
})();
